/**
 * Tabela de Unidades com Status AVCB e Alvará
 * Similar ao documentosUnidadesTabela_v2.js do backup
 */
import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { UnitsTableResponse } from '../hooks';

const ITEMS_PER_PAGE = 20;

interface UnidadesTabelaProps {
  data: UnitsTableResponse | undefined;
  isLoading?: boolean;
  className?: string;
}

// Cores para status
const STATUS_COLORS: Record<string, string> = {
  'Vigente': 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30',
  'Vigente/Em renovação': 'text-green-600 bg-green-50 dark:bg-green-950/30',
  'Permanente': 'text-blue-600 bg-blue-50 dark:bg-blue-950/30',
  'Isento': 'text-cyan-600 bg-cyan-50 dark:bg-cyan-950/30',
  'Vencido': 'text-red-600 bg-red-50 dark:bg-red-950/30',
  'Vencido/Em renovação': 'text-orange-600 bg-orange-50 dark:bg-orange-950/30',
  'Inexistente': 'text-red-700 bg-red-100 dark:bg-red-950/40',
  'Inexistente/Em obtenção': 'text-amber-600 bg-amber-50 dark:bg-amber-950/30',
  'Não configurado': 'text-gray-500 bg-gray-100 dark:bg-gray-800/50',
};

function getStatusColor(status: string): string {
  const key = Object.keys(STATUS_COLORS).find(
    (k) => k.toLowerCase() === status.toLowerCase()
  );
  return key ? STATUS_COLORS[key] : 'text-gray-500 bg-gray-100 dark:bg-gray-800/50';
}

export function UnidadesTabela({
  data,
  isLoading = false,
  className,
}: UnidadesTabelaProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Filtra os dados baseado no termo de pesquisa
  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (!searchTerm.trim()) return data.data;
    
    const term = searchTerm.toLowerCase();
    return data.data.filter(row => 
      row.identification?.toLowerCase().includes(term) ||
      row.cnpj?.toLowerCase().includes(term) ||
      row.regional?.toLowerCase().includes(term) ||
      row.avcbStatus?.toLowerCase().includes(term) ||
      row.alvaraStatus?.toLowerCase().includes(term)
    );
  }, [data?.data, searchTerm]);

  // Calcula paginação
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset página ao mudar pesquisa
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <h3 className="text-sm font-medium text-muted-foreground mb-4">
          Unidades
        </h3>
        <div className="animate-pulse space-y-2">
          <div className="h-10 bg-muted rounded" />
          <div className="h-8 bg-muted/70 rounded" />
          <div className="h-8 bg-muted/60 rounded" />
          <div className="h-8 bg-muted/50 rounded" />
          <div className="h-8 bg-muted/40 rounded" />
        </div>
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <h3 className="text-sm font-medium text-muted-foreground mb-4">
          Unidades
        </h3>
        <div className="flex items-center justify-center h-[200px] text-muted-foreground">
          Nenhuma unidade encontrada
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
      {/* Header com título e pesquisa */}
      <div className="flex items-center justify-between mb-4 gap-4">
        <h3 className="text-sm font-medium text-muted-foreground">
          Unidades
        </h3>
        
        {/* Campo de pesquisa */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Pesquisar..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full h-8 pl-8 pr-3 text-sm bg-muted/50 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {filteredData.length.toLocaleString('pt-BR')} de {data.total.toLocaleString('pt-BR')} unidades
        </span>
      </div>
      
      <div className="overflow-auto max-h-[400px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b">
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Identificação</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">CNPJ</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Regional</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Status AVCB</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Status Alvará</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => (
              <tr 
                key={`${row.unitId}-${startIndex + index}`}
                className="border-b border-border/50 hover:bg-muted/50 transition-colors"
              >
                <td className="py-2 px-2 text-xs">{row.identification || '—'}</td>
                <td className="py-2 px-2 font-mono text-xs">{row.cnpj || '—'}</td>
                <td className="py-2 px-2 text-xs">{row.regional || '—'}</td>
                <td className="py-2 px-2">
                  <span className={cn(
                    'px-2 py-0.5 rounded-full text-xs font-medium',
                    getStatusColor(row.avcbStatus)
                  )}>
                    {row.avcbStatus}
                  </span>
                </td>
                <td className="py-2 px-2">
                  <span className={cn(
                    'px-2 py-0.5 rounded-full text-xs font-medium',
                    getStatusColor(row.alvaraStatus)
                  )}>
                    {row.alvaraStatus}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredData.length === 0 && searchTerm && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Nenhum resultado para "{searchTerm}"
          </div>
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <span className="text-xs text-muted-foreground">
            Página {currentPage} de {totalPages}
          </span>
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            {/* Números das páginas */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={cn(
                    'min-w-[28px] h-7 text-xs rounded',
                    currentPage === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  )}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
